import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Building2, KeyRound, ClipboardCheck, ChevronLeft, ChevronRight,
  MessageCircle, Compass, FileText, Handshake, BadgeCheck, Eye, Quote,
} from 'lucide-react';
import { api } from '../lib/api';
import type { Property, FeaturedLocation, Area, Testimonial } from '../types';
import VideoHero from '../components/VideoHero';
import PropertyCard from '../components/PropertyCard';

const WORK_STEPS = [
  {
    icon: <MessageCircle className="h-10 w-10 text-primary" />,
    title: 'Consultation',
    text: 'We begin with a personalised consultation to understand your goals, timeline and investment objectives, ensuring every recommendation is aligned with your vision.',
  },
  {
    icon: <Compass className="h-10 w-10 text-primary" />,
    title: 'Curation',
    text: 'We curate a carefully vetted selection of residences tailored to your brief, complete with verified documentation for a seamless and confident property search.',
  },
  {
    icon: <FileText className="h-10 w-10 text-primary" />,
    title: 'Closing',
    text: 'We manage negotiations, legal review and documentation, ensuring a seamless, transparent and secure transaction from offer to completion.',
  },
  {
    icon: <ClipboardCheck className="h-10 w-10 text-primary" />,
    title: 'Management',
    text: 'After handover, our dedicated facility management team ensures your property remains operational, secure and meticulously maintained, protecting its value over time.',
  },
];

const DIFFERENCE_POINTS = [
  {
    icon: <Handshake className="h-10 w-10 text-primary" />,
    title: 'End-to-End Service',
    text: 'From the first viewing through legal searches and final closing, we remain by your side, providing expert guidance and seamless support at every stage of your property journey.',
  },
  {
    icon: <BadgeCheck className="h-10 w-10 text-primary" />,
    title: 'Vetted Portfolio',
    text: 'Every property is carefully inspected, verified and fully documented before being presented to you, ensuring confidence, transparency and peace of mind.',
  },
  {
    icon: <Eye className="h-10 w-10 text-primary" />,
    title: 'Transparency',
    text: 'Clear pricing, verified documentation and complete transparency at every stage of your property transaction.',
  },
  {
    icon: <ClipboardCheck className="h-10 w-10 text-primary" />,
    title: 'Professional Management',
    text: 'Our dedicated facility management team keeps your asset secure, compliant and meticulously maintained all year round.',
  },
];

export default function Home() {
  const [featuredProperties, setFeaturedProperties] = useState<Property[]>([]);
  const [locations, setLocations] = useState<FeaturedLocation[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);

  useEffect(() => {
    api.get<Property[]>('/properties/featured').then(setFeaturedProperties).catch(() => {});
    api.get<FeaturedLocation[]>('/locations/featured').then(setLocations).catch(() => {});
    api.get<Area[]>('/areas').then(setAreas).catch(() => {});
    api.get<Testimonial[]>('/testimonials').then(setTestimonials).catch(() => {});
  }, []);

  const locationCover = (loc: FeaturedLocation) => loc.cover_image;

  const featuredRef = useRef<HTMLDivElement>(null);
  const scrollFeatured = (dir: number) => {
    const el = featuredRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.9, behavior: 'smooth' });
  };

  const locationsRef = useRef<HTMLDivElement>(null);
  const scrollLocations = (dir: number) => {
    const el = locationsRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.9, behavior: 'smooth' });
  };

  const testimonialsRef = useRef<HTMLDivElement>(null);
  const scrollTestimonials = (dir: number) => {
    const el = testimonialsRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.9, behavior: 'smooth' });
  };

  return (
    <div>
      {/* Hero Section */}
      <VideoHero height="h-screen" overlay="bg-black bg-opacity-30" videoKey="hero_video_home">
        <div className="max-w-7xl mx-auto px-4 h-full flex items-start pt-[33vh]">
          <div className="text-white max-w-3xl">
            <p className="uppercase tracking-[0.35em] text-primary text-xs md:text-sm font-semibold mb-4">
              Real Estate &amp; Facility Management
            </p>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-4">
              Find Your Perfect Property
            </h1>
            <p className="uppercase tracking-[0.35em] text-white text-xs md:text-sm font-semibold mb-10">
              Signature Homes For Distinguished Lives
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/sales"
                className="inline-flex items-center bg-primary text-black px-8 py-1.5 text-base md:text-lg font-semibold hover:bg-primary-light shadow-lg transition-colors"
              >
                View Properties
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center border-2 border-white text-white px-8 py-1.5 text-base md:text-lg font-semibold hover:bg-white hover:text-black transition-colors"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </VideoHero>

      {/* How We Work */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="uppercase tracking-[0.3em] text-obsidian text-sm font-semibold mb-3">How We Work</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-obsidian mb-4 capitalize">A Seamless Journey</h2>
            <p className="text-taupe max-w-2xl mx-auto leading-relaxed">
              From the first conversation to the day-to-day care of your asset, every step is handled with professionalism and meticulousness.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {WORK_STEPS.map((step, i) => (
              <div key={step.title} className="relative bg-ash-light p-8 border-t-4 border-primary hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
                <span className="absolute top-6 right-6 text-4xl font-bold text-primary/20 font-display">
                  {String(i + 1).padStart(2, '0')}
                </span>
                {step.icon}
                <h3 className="text-2xl font-semibold mb-3 text-obsidian capitalize mt-4">{step.title}</h3>
                <p className="text-charcoal text-sm leading-relaxed">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Collection */}
      <section className="bg-ash-light py-16">
        <div className="max-w-7xl mx-auto px-4">
          <p className="uppercase tracking-[0.3em] text-obsidian text-sm font-semibold text-center mb-3">The Collection</p>
          <h2 className="text-3xl md:text-4xl font-semibold text-obsidian text-center mb-8 capitalize">Featured Residences</h2>
          {featuredProperties.length === 0 ? (
            <p className="text-center text-taupe text-lg max-w-xl mx-auto leading-relaxed">
              The collection is being curated. Please check back shortly.
            </p>
          ) : (
            <div className="relative">
              {featuredProperties.length > 4 && (
                <>
                  <button
                    onClick={() => scrollFeatured(-1)}
                    className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 bg-primary text-black p-2 rounded-full shadow-lg hover:bg-primary-light transition-colors"
                    aria-label="Previous properties"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => scrollFeatured(1)}
                    className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 bg-primary text-black p-2 rounded-full shadow-lg hover:bg-primary-light transition-colors"
                    aria-label="Next properties"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              )}
              <div ref={featuredRef} className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth no-scrollbar -mx-2 px-2 pb-2">
                {featuredProperties.slice(0, 8).map((property) => (
                  <div key={property.id} className="snap-start shrink-0 w-1/2 md:w-1/4 px-2">
                    <PropertyCard property={property} />
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="text-center mt-8">
            <Link
              to="/sales"
              className="inline-flex items-center bg-primary text-black px-8 py-2 text-lg font-semibold hover:bg-primary-light transition-all duration-300"
            >
              View All Properties
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="bg-obsidian py-16">
        <div className="max-w-7xl mx-auto px-4">
          <p className="uppercase tracking-[0.3em] text-primary text-sm font-semibold text-center mb-3">What We Do</p>
          <h2 className="text-3xl md:text-4xl font-semibold text-center mb-4 text-white capitalize">Our Services</h2>
          <p className="text-taupe max-w-2xl mx-auto text-center mb-10 leading-relaxed">
            From a single signature address to a fully managed estate, we deliver the complete spectrum of premium property services.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            <Link to="/sales" className="group">
              <div className="bg-charcoal p-8 border-t-4 border-primary hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 h-full">
                <Building2 className="h-10 w-10 text-primary mb-4" />
                <h3 className="text-2xl md:text-3xl font-semibold mb-4 text-white group-hover:text-primary capitalize">Property Sales</h3>
                <p className="text-ash-light leading-relaxed">
                  Find your dream property from our exclusive collection of luxury homes and investment opportunities.
                </p>
              </div>
            </Link>
            <Link to="/leasing" className="group">
              <div className="bg-charcoal p-8 border-t-4 border-primary hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 h-full">
                <KeyRound className="h-10 w-10 text-primary mb-4" />
                <h3 className="text-2xl md:text-3xl font-semibold mb-4 text-white group-hover:text-primary capitalize">Property Leasing</h3>
                <p className="text-ash-light leading-relaxed">
                  Discover premium rental properties with flexible terms and exceptional amenities.
                </p>
              </div>
            </Link>
            <Link to="/management" className="group">
              <div className="bg-charcoal p-8 border-t-4 border-primary hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 h-full">
                <ClipboardCheck className="h-10 w-10 text-primary mb-4" />
                <h3 className="text-2xl md:text-3xl font-semibold mb-4 text-white group-hover:text-primary capitalize">Property Management</h3>
                <p className="text-ash-light leading-relaxed">
                  Professional property management services to maximize your investment returns.
                </p>
              </div>
            </Link>
          </div>
          <div className="text-center mt-10">
            <Link
              to="/management"
              className="inline-flex items-center bg-primary text-black px-8 py-2 text-lg font-semibold hover:bg-primary-light transition-colors"
            >
              Explore All Services
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* The Naya Difference */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="uppercase tracking-[0.3em] text-obsidian text-sm font-semibold mb-3">Our exceptionalism</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-obsidian mb-4 capitalize">Why Choose Us</h2>
            <p className="text-taupe max-w-2xl mx-auto leading-relaxed">
              At Naya Edwin Solutions, we combine trusted real estate expertise with professional facility management to protect and maximise the value of your property. From verified property transactions to reliable day-to-day management, we deliver transparent service, attention to detail and dependable support at every stage.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {DIFFERENCE_POINTS.map((point) => (
              <div key={point.title} className="bg-ash-light p-8 border-t-4 border-primary hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
                {point.icon}
                <h3 className="text-2xl font-semibold mb-3 text-obsidian capitalize mt-4">{point.title}</h3>
                <p className="text-charcoal text-sm leading-relaxed">{point.text}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/about"
              className="inline-flex items-center border-2 border-obsidian text-obsidian px-8 py-2 text-lg font-semibold hover:bg-obsidian hover:text-white transition-colors"
            >
              More About Us
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Client Words */}
      <section className="bg-obsidian py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="uppercase tracking-[0.3em] text-primary text-sm font-semibold mb-3">Client's Testimonies</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4 capitalize">Trusted by Discerning Clients</h2>
            <div className="w-16 h-0.5 bg-primary mx-auto" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <p className="text-5xl md:text-6xl font-bold text-primary font-display">{featuredProperties.length || 0}</p>
              <p className="uppercase tracking-[0.25em] text-taupe text-sm mt-2">Exclusive Properties</p>
            </div>
            <div className="text-center">
              <p className="text-5xl md:text-6xl font-bold text-primary font-display">{areas.length || 0}</p>
              <p className="uppercase tracking-[0.25em] text-taupe text-sm mt-2">Locations Covered</p>
            </div>
            <div className="text-center">
              <p className="text-5xl md:text-6xl font-bold text-primary font-display">03</p>
              <p className="uppercase tracking-[0.25em] text-taupe text-sm mt-2">Core Services</p>
            </div>
          </div>

          {testimonials.length > 0 && (
            <div className="relative mt-4">
              {testimonials.length > 4 && (
                <>
                  <button
                    onClick={() => scrollTestimonials(-1)}
                    className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 bg-primary text-black p-2 rounded-full shadow-lg hover:bg-primary-light transition-colors"
                    aria-label="Previous testimonials"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => scrollTestimonials(1)}
                    className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 bg-primary text-black p-2 rounded-full shadow-lg hover:bg-primary-light transition-colors"
                    aria-label="Next testimonials"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              )}
              <div ref={testimonialsRef} className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth no-scrollbar -mx-2 px-2 pb-2">
                {testimonials.slice(0, 15).map((t) => (
                  <div key={t.id} className="snap-start shrink-0 w-1/2 md:w-1/4 px-2">
                    <div className="bg-charcoal p-5 border border-primary/20 rounded-lg flex flex-col h-full hover:border-primary/60 transition-colors">
                      <Quote className="h-5 w-5 text-primary mb-2 shrink-0" />
                      <p className="text-ash-light/90 text-xs leading-relaxed mb-4 flex-1 line-clamp-5">{t.content}</p>
                      <div className="flex items-center mt-auto pt-3 border-t border-white/10">
                        {t.avatar ? (
                          <img src={t.avatar} alt={t.name} className="h-8 w-8 rounded-full object-cover mr-3 shrink-0" />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-semibold mr-3 shrink-0">
                            {t.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="text-white text-xs font-semibold capitalize truncate">{t.name}</p>
                          {t.role && <p className="text-taupe text-[10px] capitalize truncate">{t.role}</p>}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Locations Section */}
      <section className="bg-ash-light py-16">
        <div className="max-w-7xl mx-auto px-4">
          <p className="uppercase tracking-[0.3em] text-obsidian text-sm font-semibold text-center mb-3">Browse By Area</p>
          <h2 className="text-3xl md:text-4xl font-semibold text-center mb-8 text-obsidian capitalize">Featured Locations</h2>
          <div className="relative">
            {locations.length > 4 && (
              <>
                <button
                  onClick={() => scrollLocations(-1)}
                  className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 bg-primary text-black p-2 rounded-full shadow-lg hover:bg-primary-light transition-colors"
                  aria-label="Previous locations"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={() => scrollLocations(1)}
                  className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 bg-primary text-black p-2 rounded-full shadow-lg hover:bg-primary-light transition-colors"
                  aria-label="Next locations"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </>
            )}
            <div ref={locationsRef} className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth no-scrollbar -mx-2 px-2 pb-2">
              {locations.map((loc) => (
                <div key={loc.id} className="snap-start shrink-0 w-1/2 md:w-1/4 px-2">
                  <Link to={`/properties/${loc.slug}`} className="group block">
                    <div className="relative h-44 md:h-56 rounded-lg overflow-hidden">
                      {locationCover(loc) && (
                        <img
                          src={locationCover(loc) ?? undefined}
                          alt={loc.name}
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                        />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent">
                        <div className="absolute bottom-0 left-0 p-4">
                          <h3 className="text-white text-base md:text-lg font-medium capitalize">{loc.name}</h3>
                          {loc.tagline && (
                            <p className="text-primary text-xs font-medium capitalize">{loc.tagline}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
